import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import {
  getThemeGradientButtonStyle,
  onThemeGradientButtonEnter,
  onThemeGradientButtonLeave,
} from "../../utils/themeGradientButtonStyle";

const emptyForm = {
  reservationUrl: "",
  menuUrl: "",
};

const MarketingSettingsPage = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_MARKETING_SETTINGS, {
        params: { CompID },
      });
      const data = response.data?.data ?? response.data?.Data ?? {};
      setForm({
        reservationUrl: data.reservationUrl ?? data.ReservationUrl ?? "",
        menuUrl: data.menuUrl ?? data.MenuUrl ?? "",
      });
    } catch {
      showErrorToast("Failed to load marketing settings.");
      setForm(emptyForm);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!CompID) {
      showErrorToast("Company context is missing. Please sign in again.");
      return;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post(APIs.POST_ADD_UPDATE_MARKETING_SETTINGS, {
        compID: Number(CompID),
        reservationUrl: form.reservationUrl.trim() || null,
        menuUrl: form.menuUrl.trim() || null,
      });

      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Could not save marketing settings.");
        return;
      }

      showSuccessToast(response.data?.message || "Marketing settings saved successfully.");
      await loadSettings();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Something went wrong while saving marketing settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const panelInnerStyle = {
    backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
    border: isDarkMode ? "1px solid #404040" : undefined,
    color: isDarkMode ? "#ffffff" : undefined,
  };

  const gradientBtnStyle = getThemeGradientButtonStyle(themeColor);

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <h1 className="page-header mb-0">Marketing Settings</h1>
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/marketing/manageMarketingGroups">Marketing</Link>
            </li>
            <li className="breadcrumb-item active">Settings</li>
          </ol>
        </div>
      </div>

      <GridSectionLoader loading={loading || saving} minHeight="240px">
        <div className="card border-0">
          <Panel className="m-3">
            <PanelHeader>Email links</PanelHeader>
            <PanelBody>
              <div className="p-4 rounded-3 shadow-sm" style={panelInnerStyle}>
                <p className="text-muted mb-4" style={{ fontSize: "14px" }}>
                  Set company-wide reservation and menu links used by{" "}
                  <code>{`{{ReservationUrl}}`}</code> and <code>{`{{MenuUrl}}`}</code> in email and
                  SMS presets.
                </p>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Reservation URL"
                      name="reservationUrl"
                      type="url"
                      value={form.reservationUrl}
                      onchange={(e) =>
                        setForm((prev) => ({ ...prev, reservationUrl: e.target.value }))
                      }
                      placeHolder="https://onlineordering.example.com/your-restaurant/reservation"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Menu URL"
                      name="menuUrl"
                      type="url"
                      value={form.menuUrl}
                      onchange={(e) =>
                        setForm((prev) => ({ ...prev, menuUrl: e.target.value }))
                      }
                      placeHolder="https://onlineordering.example.com/your-restaurant"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                  style={gradientBtnStyle}
                  onMouseEnter={onThemeGradientButtonEnter}
                  onMouseLeave={onThemeGradientButtonLeave}
                >
                  {saving ? "Saving…" : "Save settings"}
                </button>
              </div>
            </PanelBody>
          </Panel>
        </div>
      </GridSectionLoader>
    </div>
  );
};

export default MarketingSettingsPage;
